import { Popover, Transition } from '@headlessui/react';
import { bem } from '@socialive/common';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { usePopper } from 'react-popper';
import Button from '../../Atoms/Button';
import Portal from '../../Atoms/Portal';
import './popConfirm.css';

/*
#tailwind
data-popper-placement data-popper-reference-hidden
*/

function PopConfirm({
  children,
  title,
  testId,
  confirmButtonText,
  cancelButtonText,
  confirmButtonContext,
  disabled,
  onConfirmButtonClick,
  placement,
  strategy,
  className,
  popConfirmAnchorClassName,
  xOffset,
  yOffset,
  ...other
}) {
  const [ref, setRef] = useState();
  const [popperElement, setPopperElement] = useState();
  const [arrowElement, setArrowElement] = useState(null);

  const { styles, attributes } = usePopper(ref, popperElement, {
    placement,
    strategy,
    modifiers: [
      {
        name: 'flip',
        options: {
          allowedAutoPlacements: [
            'auto',
            'auto-start',
            'auto-end',
            'top',
            'top-start',
            'top-end',
            'bottom',
            'bottom-start',
            'bottom-end',
            'right',
            'right-start',
            'right-end',
            'left',
            'left-start',
            'left-end',
          ],
        },
      },
      {
        name: 'offset',
        options: {
          offset: [xOffset, yOffset],
        },
      },
      { name: 'arrow', options: { element: arrowElement } },
    ],
  });

  const balloonTestId = testId && `${testId}-balloon`;
  const cancelButtonTestId = testId && `${testId}-cancel-button`;
  const confirmButtonTestId = testId && `${testId}-confirm-button`;

  if (disabled) return children;

  return (
    <Popover className={bem`popConfirm ${className}`} {...other}>
      {({ open }) => (
        <>
          <Popover.Button ref={setRef} as="div" className={popConfirmAnchorClassName}>
            <span>{children}</span>
          </Popover.Button>
          {open && (
            <Portal id="socialive-ui-atoms-popConfirm">
              <Transition show={open}>
                <Popover.Panel
                  ref={setPopperElement}
                  style={{ ...styles.popper, zIndex: 10000 }}
                  {...attributes.popper}
                  role="dialog"
                  className="popConfirm__balloon"
                  data-testid={balloonTestId}
                >
                  <h2 className="popConfirm__title">{title}</h2>
                  <div className="popConfirm__buttonContainer">
                    <Popover.Button as="div">
                      <Button context="subtle" size="small" data-testid={cancelButtonTestId}>
                        {cancelButtonText}
                      </Button>
                    </Popover.Button>
                    <Popover.Button as="div">
                      <Button
                        context={confirmButtonContext}
                        onClick={onConfirmButtonClick}
                        size="small"
                        data-testid={confirmButtonTestId}
                      >
                        {confirmButtonText}
                      </Button>
                    </Popover.Button>
                  </div>
                  <div ref={setArrowElement} style={styles.arrow} className="popConfirm__arrow" />
                </Popover.Panel>
              </Transition>
            </Portal>
          )}
        </>
      )}
    </Popover>
  );
}

PopConfirm.propTypes = {
  testId: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  popConfirmAnchorClassName: PropTypes.string,
  placement: PropTypes.oneOf([
    'auto',
    'auto-start',
    'auto-end',
    'top',
    'top-start',
    'top-end',
    'bottom',
    'bottom-start',
    'bottom-end',
    'right',
    'right-start',
    'right-end',
    'left',
    'left-start',
    'left-end',
  ]),
  strategy: PropTypes.oneOf(['absolute', 'fixed']),
  xOffset: PropTypes.number,
  yOffset: PropTypes.number,
  title: PropTypes.string,
  confirmButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  confirmButtonContext: PropTypes.oneOf(['primary', 'danger', 'subtle']),
  disabled: PropTypes.bool,
  onConfirmButtonClick: PropTypes.func,
};

PopConfirm.defaultProps = {
  testId: undefined,
  children: undefined,
  className: undefined,
  popConfirmAnchorClassName: undefined,
  placement: 'top',
  strategy: 'absolute',
  xOffset: 0,
  yOffset: 8,
  title: undefined,
  confirmButtonText: 'OK',
  cancelButtonText: 'Cancel',
  confirmButtonContext: 'primary',
  disabled: false,
  onConfirmButtonClick: undefined,
};

export default PopConfirm;
